import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/utils";

const timeAgo = (iso) => {
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return fmtDate(iso);
};

export function AdminNotificationBell() {
    const [data, setData] = useState({ notifications: [], unread_count: 0 });
    const [open, setOpen] = useState(false);
    const panelRef = useRef(null);
    const navigate = useNavigate();

    const load = useCallback(() => {
        api.get("/admin/notifications").then((r) => setData(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const openItem = async (n) => {
        setOpen(false);
        if (!n.read) {
            api.post(`/admin/notifications/${n.id}/read`).then(load).catch(() => {});
        }
        if (n.link) navigate(n.link);
    };

    const markAll = () => api.post("/admin/notifications/read-all").then(load).catch(() => {});

    return (
        <div className="relative" ref={panelRef}>
            <button
                data-testid="admin-notification-bell"
                onClick={() => setOpen((v) => !v)}
                className="relative p-2 rounded-lg text-phoenix-navy bg-white border border-border/70 shadow-sm hover:bg-secondary/50 transition-colors"
                aria-label="Notifications"
            >
                <Bell className="w-4 h-4" />
                {data.unread_count > 0 && (
                    <span data-testid="notification-badge"
                        className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-phoenix-orange text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {data.unread_count > 9 ? "9+" : data.unread_count}
                    </span>
                )}
            </button>

            {open && (
                <div data-testid="notification-panel"
                    className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-border/70 rounded-xl shadow-lg z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60 bg-phoenix-bg">
                        <span className="text-xs font-bold text-phoenix-navy uppercase tracking-wider">Notifications</span>
                        {data.unread_count > 0 && (
                            <button data-testid="notifications-mark-all" onClick={markAll}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-phoenix-navy">
                                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {data.notifications.length === 0 ? (
                            <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</p>
                        ) : (
                            data.notifications.map((n) => (
                                <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => openItem(n)}
                                    className={`w-full text-left px-4 py-3 border-b border-border/40 last:border-b-0 hover:bg-secondary/40 transition-colors ${!n.read ? "bg-phoenix-soft/30" : ""}`}>
                                    <div className="flex items-start gap-2">
                                        {!n.read && <span className="w-2 h-2 rounded-full bg-phoenix-orange mt-1.5 shrink-0" />}
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-phoenix-navy truncate">{n.title}</div>
                                            <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</div>
                                            <div className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</div>
                                        </div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
